/**
 * AgentCore client - AG-UI SSE streaming over fetch.
 *
 * The NovaSynth backend (FastAPI on AgentCore Runtime) implements the AG-UI
 * protocol over SSE. This module parses that stream and yields plain text
 * chunks to callers, hiding the wire format entirely.
 *
 * AG-UI event types handled:
 *   RUN_STARTED - swarm initialised, nothing to yield
 *   TEXT_MESSAGE_CONTENT - text chunk to stream to the UI
 *   RUN_FINISHED - stream complete
 *   RUN_ERROR - backend error, throw AgentCoreError
 *
 * Any unknown event type is silently ignored for forward-compatibility.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Depth controls how many agents participate and how long analyses run. */
export type AnalysisDepth = "quick" | "deep";

/**
 * Lightweight slice of the user's profile sent to the backend so the swarm can
 * personalise the threat assessment ("threat to US, and why"). Sent under
 * forwardedProps.viewer on the swarm call; the backend tolerates an empty {}.
 */
export interface Viewer {
  companyName?: string;
  industry?: string;
  companySize?: string;
  role?: string;
  competitors?: string[];
  // Optional richer personalization context (Task F). The backend persona scorer
  // tolerates extra/missing keys, so these are forwarded only when present.
  icp?: string;
  valueProps?: string;
  differentiators?: string;
  winLossNotes?: string;
  targetSegments?: string;
}

export interface AnalyzeCompetitorOptions {
  competitor: string;
  depth?: AnalysisDepth;
  idToken: string;
  sessionId: string;
  signal?: AbortSignal;
  forceRefresh?: boolean;
  /** The viewer's company context, used to personalise threat scoring. */
  viewer?: Viewer;
}

export interface AguiEvent {
  type: string;
  // TEXT_MESSAGE_CONTENT
  delta?: string;
  data?: string;
  // RUN_ERROR
  message?: string;
  error?: string;
  // TEXT_MESSAGE_START / TEXT_MESSAGE_CONTENT / TEXT_MESSAGE_END
  messageId?: string;
  // TOOL_CALL_START / TOOL_CALL_ARGS / TOOL_CALL_END
  toolCallId?: string;
  toolCallName?: string;
  // STEP_STARTED / STEP_FINISHED
  stepName?: string;
  // AGENT_TEXT_DELTA - per-agent streaming text from backend
  agentKey?: string;
  kind?: string;
  // THREAT_SCORE - fused threat assessment (synthesis layer). camelCase keys.
  competitor?: string;
  score?: number;
  level?: string;
  rationale?: string;
  whyItMattersToUs?: string;
  confidence?: number;
  dimensions?: Array<{ key?: string; label?: string; score?: number; max?: number; note?: string }>;
  topThreats?: string[];
  counterMoves?: string[];
  sources?: string[];
  // BATTLECARD - collapsible competitive brief rendered from markdown.
  markdown?: string;
  // RATE_LIMITED - the run was gently stopped because a per-user daily / per-user
  // monthly / shared daily budget was reached (or the account is paused). Emitted
  // mid-stream, followed by RUN_FINISHED (HTTP 200, no error). camelCase keys,
  // matched to the backend. scope: "day" = per-user daily, "month" = per-user
  // monthly, "global" = shared demo budget, "suspended" = account paused.
  scope?: "day" | "month" | "global" | "suspended";
  limit?: number;
  used?: number;
  resetsAt?: string; // ISO 8601 - when the reached allowance refreshes
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export class AgentCoreError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly requestId?: string
  ) {
    super(message);
    this.name = "AgentCoreError";
  }
}

export class AgentCoreAuthError extends AgentCoreError {
  constructor(requestId?: string) {
    super("Authentication failed - Cognito token may have expired. Please sign in again.", 401, requestId);
    this.name = "AgentCoreAuthError";
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getRuntimeEndpoint(): string {
  const endpoint = import.meta.env.VITE_AGENTCORE_RUNTIME_ENDPOINT as string | undefined;
  if (!endpoint) {
    throw new Error(
      "VITE_AGENTCORE_RUNTIME_ENDPOINT is not set. Check your .env.local file."
    );
  }
  return endpoint.replace(/\/$/, ""); // strip trailing slash
}

/**
 * Parse a single SSE line into an AguiEvent.
 * SSE lines are formatted as "data: <json>" or "event: <type>".
 * The AG-UI protocol sends self-contained JSON objects on each data line.
 */
function parseSseLine(line: string): AguiEvent | null {
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith(":")) return null; // keepalive or comment

  if (trimmed.startsWith("data: ")) {
    const json = trimmed.slice(6);
    if (json === "[DONE]") return { type: "RUN_FINISHED" };
    try {
      return JSON.parse(json) as AguiEvent;
    } catch {
      // Malformed JSON on the stream is logged but not fatal
      console.warn("[agentcore] Failed to parse SSE line:", json);
      return null;
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Main streaming function
// ---------------------------------------------------------------------------

/**
 * Streams a competitor analysis from the NovaSynth AgentCore Runtime.
 *
 * Yields incremental text chunks as they arrive via AG-UI SSE. The caller
 * is responsible for concatenating chunks into a final document.
 *
 * @example
 * ```ts
 * const controller = new AbortController();
 * for await (const chunk of analyzeCompetitor({ competitor, accessToken, sessionId, signal: controller.signal })) {
 *   setOutput(prev => prev + chunk);
 * }
 * ```
 */
export async function* analyzeCompetitor(
  options: AnalyzeCompetitorOptions
): AsyncGenerator<string> {
  const { competitor, depth = "quick", idToken, sessionId, signal, forceRefresh = false } = options;

  const endpoint = `${getRuntimeEndpoint()}/invocations?qualifier=DEFAULT`;

  // AG-UI RunAgentInput - camelCase field names required by ag_ui.core.types.
  // threadId = stable session (multi-turn memory correlation).
  // runId    = unique per invocation (one UUID per Analyze click).
  // messages = the user's task; orchestrator reads this to start the swarm.
  const body = JSON.stringify({
    threadId: sessionId,
    runId: crypto.randomUUID(),
    state: null,
    messages: [
      {
        id: crypto.randomUUID(),
        role: "user",
        content: `Analyze competitor: ${competitor}\nDepth: ${depth}\n\nProvide a comprehensive competitive intelligence brief covering all 5 CI questions: Q1 What are they building, Q2 Who are they selling to, Q3 How are they priced, Q4 What are customers saying, Q5 What strategic moves are they making.`,
      },
    ],
    tools: [],
    context: [],
    forwardedProps: { competitor, depth, forceRefresh },
  });

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: `Bearer ${idToken}`,
        "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": sessionId,
      },
      body,
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return; // Caller cancelled - clean exit
    }
    throw new AgentCoreError(
      `Network error connecting to AgentCore Runtime: ${err instanceof Error ? err.message : String(err)}`
    );
  }

  // Surface HTTP-level errors with typed exceptions
  if (!response.ok) {
    const requestId = response.headers.get("X-Request-ID") ?? undefined;
    if (response.status === 401 || response.status === 403) {
      throw new AgentCoreAuthError(requestId);
    }
    const body = await response.text().catch(() => "");
    throw new AgentCoreError(
      `AgentCore returned HTTP ${response.status}: ${body}`,
      response.status,
      requestId
    );
  }

  if (!response.body) {
    throw new AgentCoreError("Response body is empty - SSE stream not available");
  }

  // Read the SSE stream line by line
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines (SSE messages are newline-terminated)
      const lines = buffer.split("\n");
      // Keep the last (potentially incomplete) line in the buffer
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const event = parseSseLine(line);
        if (!event) continue;

        switch (event.type) {
          case "RUN_STARTED":
            // Swarm is initialising - nothing to yield yet
            break;

          case "TEXT_MESSAGE_CONTENT": {
            const chunk = event.delta ?? event.data;
            if (chunk) yield chunk;
            break;
          }

          case "RUN_FINISHED":
            // Flush any remaining decoder buffer
            const remainder = decoder.decode();
            if (remainder) {
              const finalEvent = parseSseLine(`data: ${remainder}`);
              if (finalEvent?.type === "TEXT_MESSAGE_CONTENT") {
                const chunk = finalEvent.delta ?? finalEvent.data;
                if (chunk) yield chunk;
              }
            }
            return;

          case "RUN_ERROR": {
            const msg = event.error ?? event.message ?? "Unknown error from agent swarm";
            throw new AgentCoreError(msg);
          }

          default:
            // Forward-compatible: ignore unknown event types
            break;
        }
      }
    }
  } finally {
    // Always release the reader lock, even on error or abort
    reader.releaseLock();
  }
}

// ---------------------------------------------------------------------------
// Event-based streaming (replaces generator - gives access to all AG-UI events)
// ---------------------------------------------------------------------------

/**
 * Streams a competitor analysis and calls `onEvent` for every AG-UI event.
 *
 * Unlike `analyzeCompetitor` (which yields only text chunks), this gives callers
 * visibility into tool call lifecycle events (TOOL_CALL_START / TOOL_CALL_END)
 * and message boundary events (TEXT_MESSAGE_START / TEXT_MESSAGE_END) so the UI
 * can show per-agent coordination status in real-time.
 */
export async function streamSwarmRun(
  options: AnalyzeCompetitorOptions,
  onEvent: (event: AguiEvent) => void,
): Promise<void> {
  const { competitor, depth = "quick", idToken, sessionId, signal, forceRefresh = false } = options;
  const endpoint = `${getRuntimeEndpoint()}/invocations?qualifier=DEFAULT`;

  const body = JSON.stringify({
    threadId: sessionId,
    runId: crypto.randomUUID(),
    state: null,
    messages: [
      {
        id: crypto.randomUUID(),
        role: "user",
        content: `Analyze competitor: ${competitor}\nDepth: ${depth}\n\nProvide a comprehensive competitive intelligence brief covering all 5 CI questions: Q1 What are they building, Q2 Who are they selling to, Q3 How are they priced, Q4 What are customers saying, Q5 What strategic moves are they making.`,
      },
    ],
    tools: [],
    context: [],
    forwardedProps: { competitor, depth, forceRefresh },
  });

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: `Bearer ${idToken}`,
        "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": sessionId,
      },
      body,
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return;
    throw new AgentCoreError(
      `Network error connecting to AgentCore Runtime: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (!response.ok) {
    const requestId = response.headers.get("X-Request-ID") ?? undefined;
    if (response.status === 401 || response.status === 403) throw new AgentCoreAuthError(requestId);
    const body = await response.text().catch(() => "");
    throw new AgentCoreError(`AgentCore returned HTTP ${response.status}: ${body}`, response.status, requestId);
  }

  if (!response.body) throw new AgentCoreError("Response body is empty - SSE stream not available");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        const event = parseSseLine(line);
        if (!event) continue;
        onEvent(event);
        if (event.type === "RUN_FINISHED" || event.type === "RUN_ERROR") return;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ---------------------------------------------------------------------------
// Direct swarm streaming (all 4 agents always run - no LLM tool selection)
// ---------------------------------------------------------------------------

/**
 * Streams via /invocations with mode:"swarm" - routes through AgentCore Runtime
 * (which only proxies /invocations) but bypasses the Strands orchestrator so all
 * 4 agents always run in parallel unconditionally.
 */
export async function streamSwarmDirect(
  options: AnalyzeCompetitorOptions,
  onEvent: (event: AguiEvent) => void,
): Promise<void> {
  const { competitor, depth = "quick", idToken, sessionId, signal, forceRefresh = false, viewer = {} } = options;
  const endpoint = `${getRuntimeEndpoint()}/invocations?qualifier=DEFAULT`;

  const body = JSON.stringify({
    threadId: sessionId,
    runId: crypto.randomUUID(),
    state: null,
    messages: [{ id: crypto.randomUUID(), role: "user", content: `Analyze competitor: ${competitor}` }],
    tools: [],
    context: [],
    // viewer = the user's company context, so the backend can personalise the
    // threat assessment ("threat to US, and why"). Empty {} is tolerated.
    forwardedProps: { competitor, depth, mode: "swarm", forceRefresh, viewer },
  });

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: `Bearer ${idToken}`,
        "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": sessionId,
      },
      body,
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return;
    throw new AgentCoreError(
      `Network error connecting to swarm endpoint: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  if (!response.ok) {
    const requestId = response.headers.get("X-Request-ID") ?? undefined;
    if (response.status === 401 || response.status === 403) throw new AgentCoreAuthError(requestId);
    const bodyText = await response.text().catch(() => "");
    throw new AgentCoreError(`Swarm returned HTTP ${response.status}: ${bodyText}`, response.status, requestId);
  }

  if (!response.body) throw new AgentCoreError("Response body is empty - SSE stream not available");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (line.startsWith(":")) continue; // keepalive comment
        const event = parseSseLine(line);
        if (!event) continue;
        onEvent(event);
        if (event.type === "RUN_FINISHED" || event.type === "RUN_ERROR") return;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ---------------------------------------------------------------------------
// Account-tied profile (onboarding persistence)
// ---------------------------------------------------------------------------

export interface RemoteProfileResult {
  ok: boolean;
  onboarded?: boolean;
  profile?: unknown;
  error?: string;
}

/**
 * Profile get/save over /invocations (mode:"profile"). AgentCore Runtime only
 * proxies /invocations, so the account-tied profile store rides this endpoint
 * (a JSON request/response, NOT the SSE stream). Keyed server-side by the
 * Cognito 'sub' decoded from the ID token, so onboarding follows the account
 * across browsers/devices/origins instead of living in per-origin localStorage.
 *
 * Never throws: returns {ok:false} on any failure so the caller can fall back to
 * its localStorage cache without blocking the user.
 */
async function profileOp(
  idToken: string,
  op: "get" | "save",
  profile?: unknown,
): Promise<RemoteProfileResult> {
  const endpoint = `${getRuntimeEndpoint()}/invocations?qualifier=DEFAULT`;
  const body = JSON.stringify({
    threadId: crypto.randomUUID(),
    runId: crypto.randomUUID(),
    state: null,
    messages: [],
    tools: [],
    context: [],
    // Identity comes ONLY from the Authorization bearer below (gateway-validated +
    // header-allowlisted, then verified in the container). We deliberately do NOT
    // put the token in the body - a single source of truth is the secure design.
    forwardedProps: { mode: "profile", op, ...(profile !== undefined ? { profile } : {}) },
  });

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body,
    });
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }

  if (!response.ok) {
    return { ok: false, error: `HTTP ${response.status}` };
  }
  try {
    return (await response.json()) as RemoteProfileResult;
  } catch {
    return { ok: false, error: "invalid JSON response" };
  }
}

export function fetchProfileRemote(idToken: string): Promise<RemoteProfileResult> {
  return profileOp(idToken, "get");
}

export function saveProfileRemote(idToken: string, profile: unknown): Promise<RemoteProfileResult> {
  return profileOp(idToken, "save", profile);
}

// ---------------------------------------------------------------------------
// Run history (read-only - the SERVER writes snapshots on run completion)
// ---------------------------------------------------------------------------

/**
 * A persisted snapshot of one completed analysis run. The backend writes these
 * automatically when a run finishes (keyed by the Cognito 'sub'); the client
 * only ever READS them - there is no client-side write path.
 *
 * Shape is LOCKED to the backend contract - do not change field names.
 */
export interface RunSnapshot {
  runId: string;
  competitor: string;
  runAt: string; // ISO 8601
  score: number; // 1-10
  level: "CRITICAL" | "HIGH" | "MEDIUM" | "LOW";
  rationale: string;
  whyItMattersToUs?: string;
  topThreats: string[];
  counterMoves: string[];
  dimensions: { key: string; label: string; score: number; max: number; note: string }[];
  sourceCount: number;
  /** Source URLs behind the assessment. Persisted by the backend so a
   *  history-opened brief + its export can show real sources. May be absent on
   *  older snapshots written before the backend started persisting URLs. */
  sources?: string[];
  confidence?: number;
}

export interface RunsListResult {
  ok: boolean;
  runs?: RunSnapshot[];
  error?: string;
}

export interface RunGetResult {
  ok: boolean;
  run?: RunSnapshot;
  error?: string;
}

/**
 * Shared JSON request to /invocations for the run-history store. Mirrors
 * profileOp: identity comes ONLY from the Authorization bearer, never the body.
 * Never throws - returns the raw parsed object (or {ok:false}) so the caller can
 * degrade gracefully when the runs table isn't configured (local dev).
 */
async function runsOp(
  idToken: string,
  forwarded: Record<string, unknown>,
): Promise<unknown> {
  const endpoint = `${getRuntimeEndpoint()}/invocations?qualifier=DEFAULT`;
  const body = JSON.stringify({
    threadId: crypto.randomUUID(),
    runId: crypto.randomUUID(),
    state: null,
    messages: [],
    tools: [],
    context: [],
    forwardedProps: { mode: "runs", ...forwarded },
  });

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body,
    });
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }

  if (!response.ok) return { ok: false, error: `HTTP ${response.status}` };
  try {
    return await response.json();
  } catch {
    return { ok: false, error: "invalid JSON response" };
  }
}

/** Coerce an unknown payload into a valid RunSnapshot, or null if unusable. */
function coerceSnapshot(v: unknown): RunSnapshot | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const competitor = typeof o.competitor === "string" ? o.competitor : "";
  const runAt = typeof o.runAt === "string" ? o.runAt : "";
  if (!competitor || !runAt) return null;
  const levels = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];
  const level = typeof o.level === "string" && levels.includes(o.level.toUpperCase())
    ? (o.level.toUpperCase() as RunSnapshot["level"])
    : "MEDIUM";
  const strList = (x: unknown): string[] =>
    Array.isArray(x) ? x.filter((s): s is string => typeof s === "string" && s.trim().length > 0) : [];
  const dims = Array.isArray(o.dimensions)
    ? o.dimensions
        .map((d): RunSnapshot["dimensions"][number] | null => {
          if (!d || typeof d !== "object") return null;
          const dd = d as Record<string, unknown>;
          const label = typeof dd.label === "string" ? dd.label : "";
          if (!label) return null;
          return {
            key: typeof dd.key === "string" ? dd.key : label,
            label,
            score: typeof dd.score === "number" ? dd.score : Number(dd.score) || 0,
            max: typeof dd.max === "number" ? dd.max : Number(dd.max) || 10,
            note: typeof dd.note === "string" ? dd.note : "",
          };
        })
        .filter((d): d is RunSnapshot["dimensions"][number] => d !== null)
    : [];
  return {
    runId: typeof o.runId === "string" ? o.runId : `${competitor}-${runAt}`,
    competitor,
    runAt,
    score: typeof o.score === "number" ? o.score : Number(o.score) || 0,
    level,
    rationale: typeof o.rationale === "string" ? o.rationale : "",
    whyItMattersToUs: typeof o.whyItMattersToUs === "string" ? o.whyItMattersToUs : undefined,
    topThreats: strList(o.topThreats),
    counterMoves: strList(o.counterMoves),
    dimensions: dims,
    sourceCount: typeof o.sourceCount === "number" ? o.sourceCount : Number(o.sourceCount) || 0,
    sources: strList(o.sources),
    confidence: typeof o.confidence === "number" ? o.confidence : undefined,
  };
}

/**
 * List persisted run snapshots, optionally filtered to one competitor.
 * Returns {ok:false} (never throws) when the store is unreachable / unconfigured.
 */
export async function fetchRunsRemote(
  idToken: string,
  opts: { competitor?: string; limit?: number } = {},
): Promise<RunsListResult> {
  const forwarded: Record<string, unknown> = { op: "list" };
  if (opts.competitor) forwarded.competitor = opts.competitor;
  if (typeof opts.limit === "number") forwarded.limit = opts.limit;
  const res = await runsOp(idToken, forwarded);
  const o = (res ?? {}) as Record<string, unknown>;
  if (o.ok !== true) {
    return { ok: false, error: typeof o.error === "string" ? o.error : undefined };
  }
  const runs = Array.isArray(o.runs)
    ? o.runs.map(coerceSnapshot).filter((r): r is RunSnapshot => r !== null)
    : [];
  return { ok: true, runs };
}

/** Fetch a single run snapshot by id. Returns {ok:false} on any failure. */
export async function fetchRunRemote(idToken: string, runId: string): Promise<RunGetResult> {
  const res = await runsOp(idToken, { op: "get", runId });
  const o = (res ?? {}) as Record<string, unknown>;
  if (o.ok !== true) {
    return { ok: false, error: typeof o.error === "string" ? o.error : undefined };
  }
  const run = coerceSnapshot(o.run);
  return run ? { ok: true, run } : { ok: false, error: "missing run" };
}

// ---------------------------------------------------------------------------
// Semantic brief search (read-only - searches the user's OWN past briefs)
// ---------------------------------------------------------------------------

/**
 * One semantically-matched past brief. Shape is LOCKED to the backend contract
 * (forwardedProps {mode:"search"}) - do not rename fields.
 *
 *   competitor - the company the brief is about (clicks through to its detail page).
 *   date       - ISO date (YYYY-MM-DD) the brief was produced.
 *   snippet    - a short relevant excerpt of the brief text.
 *   score      - similarity 0-1 (higher = closer match).
 */
export interface BriefSearchResult {
  competitor: string;
  date: string;
  snippet: string;
  score: number;
}

/** Coerce one unknown payload into a BriefSearchResult, or null if unusable. */
function coerceBriefResult(v: unknown): BriefSearchResult | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const competitor = typeof o.competitor === "string" ? o.competitor.trim() : "";
  if (!competitor) return null;
  const score = typeof o.score === "number" ? o.score : Number(o.score) || 0;
  return {
    competitor,
    date: typeof o.date === "string" ? o.date : "",
    snippet: typeof o.snippet === "string" ? o.snippet : "",
    score: Number.isFinite(score) ? score : 0,
  };
}

/**
 * Semantic search across the signed-in user's OWN past briefs over /invocations
 * (forwardedProps {mode:"search", op:"query", q, limit}). Mirrors fetchRunsRemote:
 * identity comes ONLY from the Authorization bearer, never the body, and the backend
 * scopes results to this user's briefs.
 *
 * Never throws. Returns null when the feature is unconfigured (the backend replies
 * {ok:false}) or on any network/parse failure, so the caller can degrade silently
 * (no search results, no crash) in local dev.
 */
export async function searchBriefsRemote(
  idToken: string,
  q: string,
  limit?: number,
): Promise<BriefSearchResult[] | null> {
  const query = (q ?? "").trim();
  if (!query) return [];

  const endpoint = `${getRuntimeEndpoint()}/invocations?qualifier=DEFAULT`;
  const forwarded: Record<string, unknown> = { mode: "search", op: "query", q: query };
  if (typeof limit === "number") forwarded.limit = limit;
  const body = JSON.stringify({
    threadId: crypto.randomUUID(),
    runId: crypto.randomUUID(),
    state: null,
    messages: [],
    tools: [],
    context: [],
    forwardedProps: forwarded,
  });

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body,
    });
  } catch {
    return null;
  }

  if (!response.ok) return null;
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return null;
  }
  const o = (parsed ?? {}) as Record<string, unknown>;
  if (o.ok !== true) return null;
  const results = Array.isArray(o.results)
    ? o.results.map(coerceBriefResult).filter((r): r is BriefSearchResult => r !== null)
    : [];
  return results;
}

// ---------------------------------------------------------------------------
// Usage / daily allowance (read-only)
// ---------------------------------------------------------------------------

/**
 * The viewer's daily analysis allowance, read from the backend. Shape is LOCKED
 * to the backend contract (camelCase) - do not rename fields.
 *
 *   tier      - the account tier label (e.g. "demo").
 *   status    - "active" while the account can run; "suspended" when paused.
 *   daily.used        - analyses already run today.
 *   daily.limit       - the day's total allowance.
 *   daily.remaining   - allowance still left today.
 *   daily.resetsAt    - ISO 8601 timestamp for when the daily allowance refreshes.
 *   monthly.used      - analyses already run this calendar month.
 *   monthly.limit     - the month's total allowance.
 *   monthly.remaining - allowance still left this month.
 *   monthly.resetsAt  - ISO 8601 timestamp (first of next month, UTC) when it refreshes.
 */
export interface UsageWindow {
  used: number;
  limit: number;
  remaining: number;
  resetsAt: string;
}

export interface UsageInfo {
  tier: string;
  status: "active" | "suspended";
  /** When true the account has no per-day / per-month cap (admin / internal). The
   *  UI then shows "Unlimited analyses" instead of counts, and never gates. */
  unlimited?: boolean;
  daily: UsageWindow;
  monthly: UsageWindow;
}

/** Coerce one usage window ({used,limit,remaining,resetsAt}) defensively. */
function coerceWindow(v: unknown): UsageWindow {
  const w = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  const num = (x: unknown): number => {
    const n = typeof x === "number" ? x : Number(x);
    return Number.isFinite(n) ? n : 0;
  };
  const resetsAt = typeof w.resetsAt === "string" ? w.resetsAt : "";
  const used = num(w.used);
  const limit = num(w.limit);
  // remaining may be omitted - derive it defensively, clamped >= 0.
  const remaining = w.remaining !== undefined ? num(w.remaining) : Math.max(0, limit - used);
  return { used, limit, remaining: Math.max(0, remaining), resetsAt };
}

/** Coerce an unknown payload into a valid UsageInfo, or null if unusable. */
function coerceUsage(v: unknown): UsageInfo | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  return {
    tier: typeof o.tier === "string" ? o.tier : "",
    status: o.status === "suspended" ? "suspended" : "active",
    unlimited: o.unlimited === true,
    daily: coerceWindow(o.daily),
    // monthly may be absent on an older backend - coerceWindow yields a safe
    // all-zero window (limit 0), which the UI treats as "no monthly constraint".
    monthly: coerceWindow(o.monthly),
  };
}

/**
 * Read the viewer's daily analysis allowance over /invocations
 * (forwardedProps {mode:"usage", op:"get"}). Mirrors fetchRunsRemote: identity
 * comes ONLY from the Authorization bearer, never the body.
 *
 * Never throws. Returns null when the feature is unconfigured (the backend
 * replies {ok:false}) or on any network/parse failure, so the caller can degrade
 * silently (no quota chip, never blocks the user) in local dev.
 */
export async function fetchUsageRemote(idToken: string): Promise<UsageInfo | null> {
  const endpoint = `${getRuntimeEndpoint()}/invocations?qualifier=DEFAULT`;
  const body = JSON.stringify({
    threadId: crypto.randomUUID(),
    runId: crypto.randomUUID(),
    state: null,
    messages: [],
    tools: [],
    context: [],
    forwardedProps: { mode: "usage", op: "get" },
  });

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body,
    });
  } catch {
    return null;
  }

  if (!response.ok) return null;
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return null;
  }
  const o = (parsed ?? {}) as Record<string, unknown>;
  if (o.ok !== true) return null;
  return coerceUsage(o);
}

// ---------------------------------------------------------------------------
// Delivery actions - email a brief / Slack test + send (write actions)
// ---------------------------------------------------------------------------

/** Result of a delivery action. Never thrown - always returned. */
export interface DeliveryResult {
  ok: boolean;
  /** Where the email landed (echoed by the backend on success). */
  sentTo?: string;
  /** Friendly, non-technical error when ok === false. */
  error?: string;
}

/**
 * Shared JSON POST to /invocations for a delivery action (email / Slack).
 * Mirrors profileOp / runsOp: identity comes ONLY from the Authorization bearer,
 * never the body, and we NEVER throw - any failure returns {ok:false} so the
 * caller can show a calm inline error instead of crashing the brief view.
 *
 * Security: the Slack webhook is read SERVER-SIDE from the saved profile, so it
 * is never sent on this request and never logged here.
 */
async function deliveryOp(
  idToken: string,
  forwarded: Record<string, unknown>,
): Promise<DeliveryResult> {
  const endpoint = `${getRuntimeEndpoint()}/invocations?qualifier=DEFAULT`;
  const body = JSON.stringify({
    threadId: crypto.randomUUID(),
    runId: crypto.randomUUID(),
    state: null,
    messages: [],
    tools: [],
    context: [],
    forwardedProps: forwarded,
  });

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body,
    });
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }

  if (!response.ok) return { ok: false, error: `HTTP ${response.status}` };
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return { ok: false, error: "invalid JSON response" };
  }
  const o = (parsed ?? {}) as Record<string, unknown>;
  return {
    ok: o.ok === true,
    sentTo: typeof o.sentTo === "string" ? o.sentTo : undefined,
    error: typeof o.error === "string" ? o.error : undefined,
  };
}

/**
 * Email the competitor brief to the signed-in user's inbox.
 * Backend contract: forwardedProps {mode:"email", op:"brief", competitor}
 *   -> {ok:true, sentTo} | {ok:false, error}
 */
export function emailBriefRemote(idToken: string, competitor: string): Promise<DeliveryResult> {
  return deliveryOp(idToken, { mode: "email", op: "brief", competitor });
}

/**
 * Send a test message to the user's connected Slack channel (verifies the saved
 * webhook works). Backend contract: forwardedProps {mode:"slack", op:"test"}
 *   -> {ok:true} | {ok:false, error}
 */
export function slackTestRemote(idToken: string): Promise<DeliveryResult> {
  return deliveryOp(idToken, { mode: "slack", op: "test" });
}

/**
 * Send the competitor brief to the user's connected Slack channel.
 * Backend contract: forwardedProps {mode:"slack", op:"brief", competitor}
 *   -> {ok:true} | {ok:false, error}
 */
export function slackBriefRemote(idToken: string, competitor: string): Promise<DeliveryResult> {
  return deliveryOp(idToken, { mode: "slack", op: "brief", competitor });
}

// ---------------------------------------------------------------------------
// SEC 8-K filing alerts - subscribe / unsubscribe a competitor to email +
// Slack alerts on new high-signal SEC filings (leadership change, M&A, funding,
// material cybersecurity incident, financial distress).
//
// Identity comes ONLY from the Authorization bearer. The email address and the
// Slack webhook are read SERVER-SIDE from the saved profile, so they are never
// sent on these requests and never logged here. Like the delivery helpers,
// these NEVER throw - any failure returns {ok:false} so the caller can revert
// the switch and show a calm inline note.
// ---------------------------------------------------------------------------

/** Result of an alert subscribe/unsubscribe action. Extends DeliveryResult with
 *  the subscription state the backend echoes back, plus whether Slack delivery
 *  is wired up for this user. */
export interface AlertActionResult extends DeliveryResult {
  /** True once the competitor is subscribed (echoed by the backend). */
  subscribed?: boolean;
  /** True when the user has a connected Slack channel that will also receive alerts. */
  slackLinked?: boolean;
}

/** Shared POST for an alert subscribe/unsubscribe op. Mirrors deliveryOp but
 *  also surfaces the `subscribed` / `slackLinked` flags the alerts mode returns. */
async function alertOp(
  idToken: string,
  forwarded: Record<string, unknown>,
): Promise<AlertActionResult> {
  const endpoint = `${getRuntimeEndpoint()}/invocations?qualifier=DEFAULT`;
  const body = JSON.stringify({
    threadId: crypto.randomUUID(),
    runId: crypto.randomUUID(),
    state: null,
    messages: [],
    tools: [],
    context: [],
    forwardedProps: forwarded,
  });

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body,
    });
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }

  if (!response.ok) return { ok: false, error: `HTTP ${response.status}` };
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return { ok: false, error: "invalid JSON response" };
  }
  const o = (parsed ?? {}) as Record<string, unknown>;
  return {
    ok: o.ok === true,
    subscribed: typeof o.subscribed === "boolean" ? o.subscribed : undefined,
    slackLinked: typeof o.slackLinked === "boolean" ? o.slackLinked : undefined,
    error: typeof o.error === "string" ? o.error : undefined,
  };
}

/**
 * Subscribe the signed-in user to SEC 8-K alerts for a competitor.
 * Backend contract: forwardedProps {mode:"alerts", op:"subscribe", competitor}
 *   -> {ok:true, subscribed:true, competitor, slackLinked} on success
 *   -> {ok:false, subscribed:false, error} when the competitor is NOT a public
 *      US SEC filer (private / non-US companies do not file 8-Ks). The caller
 *      should surface `error` and leave the toggle OFF.
 */
export function subscribeAlertRemote(idToken: string, competitor: string): Promise<AlertActionResult> {
  return alertOp(idToken, { mode: "alerts", op: "subscribe", competitor });
}

/**
 * Unsubscribe the signed-in user from SEC 8-K alerts for a competitor.
 * Backend contract: forwardedProps {mode:"alerts", op:"unsubscribe", competitor}
 *   -> {ok:true, subscribed:false, competitor}
 */
export function unsubscribeAlertRemote(idToken: string, competitor: string): Promise<AlertActionResult> {
  return alertOp(idToken, { mode: "alerts", op: "unsubscribe", competitor });
}

/** The viewer's current SEC 8-K alert subscriptions. */
export interface AlertSubscriptions {
  ok: boolean;
  /** Display names this user is subscribed to (empty on failure). */
  subscriptions: string[];
  /** Whether the backend monitor that fires these alerts is currently enabled. */
  monitorEnabled: boolean;
}

/**
 * Read the viewer's SEC 8-K alert subscriptions over /invocations
 * (forwardedProps {mode:"alerts", op:"list"}). Mirrors fetchUsageRemote:
 * identity comes ONLY from the Authorization bearer, never the body.
 *
 * Never throws. Returns {ok:false, subscriptions:[], monitorEnabled:false} on
 * any network/parse failure or when the feature is unconfigured, so the caller
 * can degrade silently (toggle simply starts OFF) in local dev.
 */
export async function fetchAlertSubscriptionsRemote(idToken: string): Promise<AlertSubscriptions> {
  const fail: AlertSubscriptions = { ok: false, subscriptions: [], monitorEnabled: false };
  const endpoint = `${getRuntimeEndpoint()}/invocations?qualifier=DEFAULT`;
  const body = JSON.stringify({
    threadId: crypto.randomUUID(),
    runId: crypto.randomUUID(),
    state: null,
    messages: [],
    tools: [],
    context: [],
    forwardedProps: { mode: "alerts", op: "list" },
  });

  let response: Response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body,
    });
  } catch {
    return fail;
  }

  if (!response.ok) return fail;
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    return fail;
  }
  const o = (parsed ?? {}) as Record<string, unknown>;
  if (o.ok !== true) return fail;
  const subs = Array.isArray(o.subscriptions)
    ? o.subscriptions.filter((s): s is string => typeof s === "string")
    : [];
  return {
    ok: true,
    subscriptions: subs,
    monitorEnabled: o.monitorEnabled === true,
  };
}

// ---------------------------------------------------------------------------
// Health check
// ---------------------------------------------------------------------------

/**
 * Pings the AgentCore Runtime health endpoint.
 * Useful for pre-flight checks before starting an analysis.
 */
export async function pingRuntime(): Promise<boolean> {
  try {
    const endpoint = `${getRuntimeEndpoint()}/ping`;
    const res = await fetch(endpoint, { method: "GET", signal: AbortSignal.timeout(5000) });
    return res.ok;
  } catch {
    return false;
  }
}
